import React from "react";
import styled from "@emotion/styled";

import * as tokens from "../../design-tokens";
import { Typography } from "../Typography";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";
import { Button } from "../Button";

const NavigationButton = ({
  direction,
  disabled,
  onNavigation,
}: {
  direction: "past" | "future";
  onNavigation: () => void;
  disabled?: boolean;
}) => {
  const isPastDirection = direction === "past";

  const iconName = isPastDirection
    ? SvgNames.ChevronLeft
    : SvgNames.ChevronRight;

  return (
    <Button
      color="tertiary"
      style={{ padding: 0 }}
      aria-label={`button-nav-${direction}`}
      disabled={disabled}
      onClick={() => onNavigation()}
      iconMiddle={
        <SvgIcon
          data-testid="testIcon"
          name={iconName}
          width={24}
          height={24}
          color={tokens.BRAND_HEROBLUE}
        />
      }
    />
  );
};

const MonthHeader = ({
  dateString,
  disabledFutureNavigation,
  disabledPastNavigation,
  onPrevious,
  onNext,
}: MonthHeaderProps) => {
  const formattedMonth = new Date(dateString).toLocaleString("en-US", {
    month: "long",
  });

  return (
    <StyledMonthHeader>
      <DateWithNavigation
        key={`${formattedMonth}-bidirectional`}
        direction="bidirectional"
      >
        <NavigationButton
          aria-label={`${formattedMonth}-navigate-past`}
          direction="past"
          onNavigation={onPrevious}
          disabled={disabledPastNavigation}
        />
        <StyledGrid>
          <Typography key={formattedMonth} align="center">
            {formattedMonth}
          </Typography>
        </StyledGrid>
        <NavigationButton
          aria-label={`${formattedMonth}-navigate-future`}
          direction="future"
          onNavigation={onNext}
          disabled={disabledFutureNavigation}
        />
      </DateWithNavigation>
    </StyledMonthHeader>
  );
};

type MonthHeaderProps = {
  dateString: string;
  disabledPastNavigation?: boolean;
  disabledFutureNavigation?: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

const StyledMonthHeader = styled.div`
  display: grid;
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  padding-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
`;

const DateWithNavigation = styled.div<{
  direction: "past" | "future" | "bidirectional";
}>`
  display: flex;
  flex-direction: ${(props) =>
    ["bidirectional", "past"].includes(props.direction)
      ? "row"
      : "row-reverse"};

  justify-content: space-between;
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const memoizedMonthHeader = React.memo(MonthHeader);

export type { MonthHeaderProps };
export { memoizedMonthHeader as MonthHeader };
